import { DoctorSimple, DoctorSimpleFromCurrentHealthcareProviderQuery } from '@dreryk/egabinet-personnel-frontend-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import {
   ReceptionServiceWithPractitionersSimple,
   ReceptionServiceWithPractitionersSimpleQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

type AddPrescriptionVisitModalLoaderRenderProps = {
   practitioners: DoctorSimple[];
   healthcareProviderLocations: HealthcareProviderLocation[];
   receptionServices: ReceptionServiceWithPractitionersSimple[];
};

type AddPrescriptionVisitModalLoaderProps = {
   readonly children: (renderProps: AddPrescriptionVisitModalLoaderRenderProps) => JSX.Element;
};

export const AddPrescriptionVisitModalLoader = (props: AddPrescriptionVisitModalLoaderProps): JSX.Element => {
   const privateMedicalServicesEnabled = protectFeature('searchingPrivateMedicalServices', true, false);

   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<DoctorSimple>(() => new DoctorSimpleFromCurrentHealthcareProviderQuery());

   const {
      data: healthcareProviderLocations,
      loading: locationsLoading,
      error: locationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: receptionServices,
      loading: receptionServicesLoading,
      error: receptionServicesError,
   } = useCqrsSearch<ReceptionServiceWithPractitionersSimple>(
      () => new ReceptionServiceWithPractitionersSimpleQuery(),
      paginations.all(),
      { enabled: privateMedicalServicesEnabled }
   );

   if (practitionersLoading || locationsLoading || receptionServicesLoading) {
      return <LoaderSpinner />;
   }

   if (practitionersError || locationsError || receptionServicesError) {
      return (
         <Message
            testId={'addPrescriptionVisitModalFetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      receptionServices: privateMedicalServicesEnabled ? receptionServices : [],
   });
};
